import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import TelegramIcon from "@mui/icons-material/Telegram";
import {
  alpha,
  Box,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  getFrontendAppBaseUrl,
  getPublicMarketingBaseUrl,
} from "../../../infrastructure/utils/referralTracking";
import PanelInfo from "../shared/panel-info";

const TEAL_DEEP = "#044F79";
const TEAL_MID = "#0A7EA4";

const FOOTER_DESCRIPTION =
  "سولارنت، پلتفرمی جامع و نوآورانه است که با هدف توسعه نیروگاه‌های خورشیدی خانگی، مالکان، سرمایه‌گذاران و پیمانکاران را در یک بستر دیجیتال و شفاف گرد هم می‌آورد. این پلتفرم با ساده‌سازی فرآیندها، مسیر دسترسی به انرژی پاک، درآمد پایدار و آینده‌ای سبز برای ایران را هموار می‌کند.";

const ENAMAD_URL =
  "https://trustseal.enamad.ir/?id=645666&Code=I4F12To5U9I6fHwPYN4zHJpfDssG8O8d";
const ENAMAD_IMG =
  "https://trustseal.enamad.ir/logo.aspx?id=645666&Code=I4F12To5U9I6fHwPYN4zHJpfDssG8O8d";

const SITE_LOGO_URL =
  "https://solarent.ir/wp-content/uploads/2025/06/%D8%B3%D8%A7%DB%8C%D8%AA-01-01-2.png";

const FOOTER_LINKS = [
  { label: "خدمات", path: "/services/" },
  { label: "حساب کاربری", app: true },
  { label: "بلاگ", path: "/blog/" },
  { label: "تماس با ما", path: "/contact-us/" },
  { label: "درباره ما", path: "/about-us/" },
];

const CONTACT_ITEMS = [
  {
    icon: LocationOnOutlinedIcon,
    label: "آدرس",
    value: "اصفهان، سپاهان‌شهر، میدان غدیر، مجتمع هفت‌نگین، طبقه اول، واحد ۲۸۴",
  },
  {
    icon: PhoneOutlinedIcon,
    label: "تلفن",
    value: "۰۹۱۳۵۵۱۷۸۳۱",
    href: "tel:09135517831",
  },
  {
    icon: PhoneOutlinedIcon,
    label: "تلفن ثابت",
    value: "۰۳۱-۵۳۶۰۰۶۰۱",
    href: "tel:03153600601",
  },
  {
    icon: EmailOutlinedIcon,
    label: "ایمیل",
    value: "info@solarent.ir",
    href: "mailto:info@solarent.ir",
  },
];

function FooterNavLink({ item }) {
  const marketingBase = getPublicMarketingBaseUrl();
  const href = item.app
    ? getFrontendAppBaseUrl()
    : `${marketingBase}${item.path}`;

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{
        color: "text.secondary",
        fontSize: "0.875rem",
        fontWeight: 500,
        "&:hover": { color: TEAL_MID },
      }}
    >
      {item.label}
    </Link>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const content = (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          bgcolor: alpha(TEAL_MID, 0.1),
          color: TEAL_DEEP,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: 0.15,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
          {label}
        </Typography>
        <Typography
          variant="body2"
          color="text.primary"
          lineHeight={1.75}
          sx={{ fontSize: "0.84rem" }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );

  if (!href) {
    return content;
  }

  return (
    <Link
      href={href}
      underline="none"
      color="inherit"
      sx={{ "&:hover .MuiTypography-body2": { color: TEAL_MID } }}
    >
      {content}
    </Link>
  );
}

const SolarentSiteFooter = ({ compact = false }) => {
  const marketingBase = getPublicMarketingBaseUrl();
  const [logoFailed, setLogoFailed] = useState(false);

  if (compact) {
    return (
      <Box
        component="footer"
        dir="rtl"
        sx={{
          mt: 2,
          bgcolor: "#fff",
          borderTop: `1px solid ${alpha(TEAL_DEEP, 0.1)}`,
          height: 64,
          maxHeight: 80,
          overflow: "hidden",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            py: 0,
            px: { xs: 1.5, sm: 2 },
          }}
        >
          <Stack
            direction="row"
            spacing={{ xs: 1.25, sm: 2 }}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="nowrap"
            sx={{ width: "100%", minHeight: 44 }}
          >
            <Link
              href={marketingBase}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}
            >
              {logoFailed ? (
                <PanelInfo size={24} />
              ) : (
                <Box
                  component="img"
                  src={SITE_LOGO_URL}
                  alt="سولارنت"
                  sx={{ height: 28, width: "auto", maxWidth: 88, objectFit: "contain" }}
                  onError={() => setLogoFailed(true)}
                />
              )}
            </Link>

            <Stack
              direction="row"
              spacing={{ xs: 1, sm: 1.5 }}
              alignItems="center"
              flexWrap="nowrap"
              sx={{ flex: 1, justifyContent: "flex-end", overflow: "hidden" }}
            >
              <Link
                href="tel:09135517831"
                underline="hover"
                sx={{ color: "text.secondary", fontSize: "0.75rem", whiteSpace: "nowrap" }}
              >
                ۰۹۱۳۵۵۱۷۸۳۱
              </Link>
              <Typography component="span" color="divider" sx={{ fontSize: "0.75rem" }}>
                ·
              </Typography>
              <Link
                href="mailto:info@solarent.ir"
                underline="hover"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                  display: { xs: "none", sm: "inline" },
                }}
              >
                info@solarent.ir
              </Link>
              <Typography component="span" color="divider" sx={{ fontSize: "0.75rem", display: { xs: "none", sm: "inline" } }}>
                ·
              </Typography>
              <Link
                href={marketingBase}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ color: TEAL_DEEP, fontSize: "0.75rem", fontWeight: 700, whiteSpace: "nowrap" }}
              >
                solarent.ir
              </Link>
            </Stack>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      dir="rtl"
      sx={{
        mt: { xs: 4, md: 5 },
        bgcolor: "#fff",
        borderTop: `1px solid ${alpha(TEAL_DEEP, 0.12)}`,
        boxShadow: `0 -8px 32px ${alpha(TEAL_DEEP, 0.06)}`,
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 3.5, md: 4.5 }, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 3, sm: 3.5, md: 4 }} sx={{ mx: 0, width: "100%" }}>
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Link
                href={marketingBase}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{ display: "inline-flex", alignItems: "center", gap: 1.25 }}
              >
                {logoFailed ? (
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: alpha(TEAL_MID, 0.12),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PanelInfo size={32} />
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={SITE_LOGO_URL}
                    alt="سولارنت"
                    sx={{
                      height: 48,
                      width: "auto",
                      maxWidth: 140,
                      objectFit: "contain",
                    }}
                    onError={() => setLogoFailed(true)}
                  />
                )}
              </Link>
              <Typography variant="body2" color="text.secondary" lineHeight={1.9}>
                {FOOTER_DESCRIPTION}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              color={TEAL_DEEP}
              sx={{ mb: 1.5 }}
            >
              لینک‌ها
            </Typography>
            <Stack spacing={1.1}>
              {FOOTER_LINKS.map((item) => (
                <FooterNavLink key={item.label} item={item} />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              color={TEAL_DEEP}
              sx={{ mb: 1.5 }}
            >
              اطلاعات تماس
            </Typography>
            <Stack spacing={1.75}>
              {CONTACT_ITEMS.map((item) => (
                <ContactRow key={`${item.label}-${item.value}`} {...item} />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              color={TEAL_DEEP}
              sx={{ mb: 1.5 }}
            >
              نماد اعتماد الکترونیکی
            </Typography>
            <Link
              href={ENAMAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              referrerPolicy="origin"
              sx={{ display: "inline-block" }}
            >
              <Box
                component="img"
                src={ENAMAD_IMG}
                alt="نماد اعتماد الکترونیکی"
                referrerPolicy="origin"
                sx={{ width: 96, height: "auto", cursor: "pointer" }}
              />
            </Link>
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 2.5, md: 3 }, borderColor: alpha(TEAL_DEEP, 0.1) }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "center", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Typography variant="body2" color="text.secondary" textAlign={{ xs: "center", sm: "right" }}>
            تمام حقوق این سایت برای{" "}
            <Link
              href={marketingBase}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ color: TEAL_MID, fontWeight: 700 }}
            >
              پلتفرم سولارنت
            </Link>{" "}
            محفوظ می‌باشد.
          </Typography>

          <Link
            href="https://t.me/solarentPlatform"
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              color: TEAL_DEEP,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              bgcolor: alpha(TEAL_MID, 0.08),
              "&:hover": { bgcolor: alpha(TEAL_MID, 0.16) },
            }}
          >
            <TelegramIcon fontSize="small" />
            <Typography variant="body2" fontWeight={700}>
              کانال تلگرام سولارنت
            </Typography>
          </Link>
        </Stack>
      </Container>
    </Box>
  );
};

export default SolarentSiteFooter;
